import { httpsCallable } from 'firebase/functions';
import { functions } from '../firebase';
import type { PlayerAction } from './types';

// Thin wrappers over the callable Cloud Functions (all in europe-west1).
// Errors thrown by callables carry a `details.message` we surface to the user.

export interface RoomHandle {
  roomId: string;
  code: string;
}

function unwrapError(err: unknown): Error {
  const e = err as { details?: { message?: string }; message?: string };
  const message = e?.details?.message ?? e?.message ?? 'Something went wrong.';
  return new Error(message);
}

// Cap how long a callable may hang. The SDK's own default is 70s, which feels
// like a frozen app when a request stalls behind a flaky network. A tighter
// bound surfaces a (retryable) error instead of an indefinite spinner — this is
// the "creating a game just hangs" symptom.
const CALL_TIMEOUT_MS = 20_000;

async function call<T>(name: string, payload: Record<string, unknown>): Promise<T> {
  try {
    const fn = httpsCallable(functions, name, { timeout: CALL_TIMEOUT_MS });
    const res = await fn(payload);
    return res.data as T;
  } catch (err) {
    throw unwrapError(err);
  }
}

export function telegramAuth(
  initData: string,
): Promise<{ token: string; name: string; username: string | null }> {
  return call('telegramAuth', { initData });
}

export function registerPush(token: string): Promise<{ ok: boolean }> {
  return call('registerPush', { token });
}

export function createRoom(
  displayName: string,
  photoURL: string | null,
  turnLimit: number,
  botDifficulty: string,
  country: string | null,
  nameColor?: string,
  frame?: string | null,
): Promise<RoomHandle> {
  return call<RoomHandle>('createRoom', {
    displayName,
    photoURL,
    turnLimit,
    botDifficulty,
    country,
    nameColor,
    frame,
  });
}

export function updateRoomSettings(
  roomId: string,
  settings: { turnLimit?: number; botDifficulty?: string; isPublic?: boolean },
): Promise<{ ok: boolean }> {
  return call('updateRoomSettings', { roomId, ...settings });
}

export function forceTimeout(roomId: string): Promise<{ ok: boolean }> {
  return call('forceTimeout', { roomId });
}

export function joinRoom(
  code: string,
  displayName: string,
  photoURL: string | null,
  country: string | null,
  nameColor?: string,
  frame?: string | null,
): Promise<RoomHandle> {
  return call<RoomHandle>('joinRoom', { code, displayName, photoURL, country, nameColor, frame });
}

/** Spend coins on a shop item; resolves with the new balance. */
export function purchaseItem(itemId: string): Promise<{ ok: boolean; coins: number }> {
  return call('purchaseItem', { itemId });
}

/** Collect the login bonus; server enforces a personal 24h cooldown. */
export function claimDailyBonus(): Promise<{
  ok: boolean;
  reward: number;
  streak: number;
  coins: number;
  /** Epoch ms when the next claim unlocks. */
  nextAt: number;
}> {
  return call('claimDailyBonus', {});
}

/** One-time Google welcome bonus; idempotent, safe to fire on every sign-in. */
export function claimGoogleBonus(): Promise<{ ok: boolean; granted: boolean; coins: number }> {
  return call('claimGoogleBonus', {});
}

/**
 * Persist an equipped cosmetic on the wallet doc, so the choice follows the
 * account instead of living only in one browser's localStorage. The server
 * re-validates ownership/unlock — an id that isn't owned/earned is rejected.
 */
export function setEquipped(
  kind: 'frame' | 'cardback' | 'nameColor',
  value: string | null,
): Promise<{ ok: boolean }> {
  return call('setEquipped', { kind, value });
}

// Public top-players leaderboard (server-written; humans only).
export interface LeaderboardEntry {
  uid: string;
  name: string;
  games: number;
  mvp: number;
  country?: string | null;
}

export function leaveRoom(roomId: string): Promise<{ ok: boolean }> {
  return call('leaveRoom', { roomId });
}

export function startGame(roomId: string): Promise<{ ok: boolean }> {
  return call('startGame', { roomId });
}

export function addBot(roomId: string): Promise<{ ok: boolean; botUid: string }> {
  return call('addBot', { roomId });
}

export function removeBot(roomId: string, botUid: string): Promise<{ ok: boolean }> {
  return call('removeBot', { roomId, botUid });
}

export function rematch(roomId: string): Promise<{ ok: boolean }> {
  return call('rematch', { roomId });
}

export function submitAction(
  roomId: string,
  action: PlayerAction,
): Promise<{ ok: boolean; winner?: string | null }> {
  return call('submitAction', { roomId, action });
}
